import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Scales, List, X } from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/sobre', label: 'Sobre' },
    { path: '/areas', label: 'Áreas de Atuação' },
    { path: '/contato', label: 'Contato' },
    { path: '/blog', label: 'Blog' },
  ];

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/90 backdrop-blur-xl border-b border-[#2A2A2A]/10 shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_bpc-loas/artifacts/ww7z8lbd_Timbre%20RDC%20-%20enquadramento%20.jpg" 
              alt="RDC Advogados"
              className="h-12 w-auto object-contain"
            />
            <div>
              <div className="text-xl font-bold text-[#2A2A2A] playfair tracking-tight">
                RDC Advogados
              </div>
              <div className="text-xs text-[#737373] ibm-plex">Rio de Janeiro</div>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#2A2A2A] border-b-2 border-[#A98C67]'
                    : 'text-[#737373] hover:text-[#2A2A2A]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <a
              href="https://wa.me/552128880258"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-whatsapp-button"
              className="bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-sm transition-colors duration-200 font-medium px-6 py-3 text-sm"
            >
              Falar no WhatsApp
            </a>
          </nav>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-[#2A2A2A] p-2"
            data-testid="mobile-menu-button"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <List size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-white border-t border-[#2A2A2A]/10"
            data-testid="mobile-menu"
          >
            <nav className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`block py-2 text-base font-medium transition-colors ${
                    location.pathname === link.path
                      ? 'text-[#2A2A2A]'
                      : 'text-[#737373] hover:text-[#2A2A2A]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://wa.me/552128880258"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-whatsapp-button"
                className="block text-center bg-[#25D366] text-white hover:bg-[#20BA5A] rounded-sm transition-colors duration-200 font-medium px-6 py-3 text-sm"
              >
                Falar no WhatsApp
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};